/**
 * Catalogue sync and serialized units.
 *
 * The sync endpoints are queries with `keepUnusedDataFor: 0` on purpose: their payload goes
 * straight into IndexedDB and is read from there. Keeping 3,000 variants in the RTK Query cache
 * would serialise them into the Redux store, and the store is not a database.
 */

import { api } from '../api.js';
import { LIST, stockTag, unitTag } from '../tags.js';
import type {
  AvailableUnitMatch,
  CatalogueSync,
  CatalogueVersion,
  InventoryUnit,
  Paged,
  Shop,
  ShopId,
  VariantId,
} from '../../types/api.js';

export const catalogueApi = api.injectEndpoints({
  endpoints: (build) => ({
    /** Full snapshot when `since` is omitted; the delta, including deactivations, when it is not. */
    syncCatalogue: build.query<CatalogueSync, { shopId: ShopId; since?: string | undefined }>({
      query: ({ shopId, since }) => ({
        url: '/catalogue/sync',
        params: since === undefined ? { shopId } : { shopId, since },
      }),
      keepUnusedDataFor: 0,
    }),

    /** Two numbers instead of a megabyte. The background refresh polls this, not the sync. */
    catalogueVersion: build.query<CatalogueVersion, { shopId: ShopId }>({
      query: ({ shopId }) => ({ url: '/catalogue/sync/version', params: { shopId } }),
      keepUnusedDataFor: 0,
    }),

    /**
     * The IMEIs available for one variant at one shop. `pageSize` is deliberately large: a
     * variant can hold sixty handsets and a picker that paginates is a picker nobody uses.
     */
    availableUnits: build.query<Paged<InventoryUnit>, { shopId: ShopId; variantId: VariantId }>({
      query: ({ shopId, variantId }) => ({
        url: '/units/available',
        params: { shopId, variantId, page: 1, pageSize: 200 },
      }),
      providesTags: (_result, _error, { shopId, variantId }) => [unitTag(shopId, variantId)],
    }),

    /**
     * Handsets in stock at one shop whose IMEI contains the typed digits — the till's main
     * search, so a phone is found by the number on its box. Never cached: an IMEI sold on another
     * till a minute ago must not be offered here.
     */
    searchAvailableUnits: build.query<readonly AvailableUnitMatch[], { shopId: ShopId; serial: string }>({
      query: ({ shopId, serial }) => ({ url: '/units/available/search', params: { shopId, serial } }),
      keepUnusedDataFor: 0,
    }),

    /** Live quantity for one variant, when the cached figure needs confirming before a sale. */
    variantStock: build.query<{ quantity: number }, { shopId: ShopId; variantId: VariantId }>({
      query: ({ shopId, variantId }) => ({ url: '/stock', params: { shopId, variantId } }),
      providesTags: (_result, _error, { shopId, variantId }) => [stockTag(shopId, variantId)],
    }),

    /**
     * The shops this user may sell from. `GET /shops` needs `shop.view`, which a salesperson does
     * not have — they still need their own shop's name and paper width. See §D.
     */
    myShops: build.query<readonly Shop[], void>({
      query: () => '/shops/mine',
      providesTags: [{ type: 'Shop', id: LIST }],
    }),
  }),
});

export const {
  useSyncCatalogueQuery,
  useLazySyncCatalogueQuery,
  useCatalogueVersionQuery,
  useAvailableUnitsQuery,
  useSearchAvailableUnitsQuery,
  useVariantStockQuery,
  useMyShopsQuery,
} = catalogueApi;
